/**
 * @fileoverview Root route layout for TanStack Router navigation
 * @module routes/__root
 */

import React, { type ReactElement } from 'react';
import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { NavigationBar } from '@/components/layout/NavigationBar';
import { VerticalBorders } from '@/components/layout/VerticalBorders';
import { useReferralTracking } from '@/features/referrals/hooks/useReferralTracking';
import type { AuthContextInterface } from '@/features/auth/types/auth';
import { AuthModalProvider } from '@/features/auth/contexts/AuthModalContext';

// Extend TanStack Router's context interface
declare module '@tanstack/react-router' {
  interface RouterContext {
    auth: AuthContextInterface;
  }
}


/**
 * Root route layout component.
 *
 * Provides the main application layout with navigation bar and
 * content area. All child routes will be rendered within this layout.
 *
 * Features:
 * - Navigation bar with wallet connection (hidden on admin routes)
 * - Main content area with consistent spacing
 * - TanStack Router devtools in development
 * - Responsive design with container max-width
 *
 * @component
 */
function RootComponent(): ReactElement {
  const location = useLocation();

  // Enable automatic referral tracking across the entire app
  useReferralTracking();

  // Check if current route is any admin route by checking pathname prefix
  const isAdminRoute = location.pathname.startsWith('/sys/mgmt');
  // Grants deck is a full-screen presentation — hide all chrome
  const isFullscreenRoute = location.pathname === '/grants-deck';

  // Update body background based on route
  React.useEffect(() => {
    if (isAdminRoute) {
      document.body.style.background = '#ffffff';
    } else {
      document.body.style.background = '';
    }

    return () => {
      document.body.style.background = '';
    };
  }, [isAdminRoute]);

  return (
    <AuthModalProvider>
      <div className={`min-h-screen ${isAdminRoute ? 'bg-white' : 'bg-background'}`}>
        {/* Global vertical borders for all routes (hidden on admin & fullscreen routes) */}
        {!isAdminRoute && !isFullscreenRoute && <VerticalBorders />}

        {!isAdminRoute && !isFullscreenRoute && <NavigationBar />}

        <main className={isAdminRoute || isFullscreenRoute ? '' : 'container mx-auto'}>
          <Outlet />
        </main>

        {/* TanStack Router Devtools - only shows in development */}
        <TanStackRouterDevtools />
      </div>
    </AuthModalProvider>
  );
}

/**
 * Root route configuration.
 *
 * Defines the root route with the main application layout.
 * All other routes will be nested within this root route.
 *
 * IMPORTANT: The loader function is required to trigger TanStack Router's
 * pending state during navigation. Without it, defaultPendingComponent
 * will never show. See: https://github.com/TanStack/router/issues/3556
 */
export const Route = createRootRoute({
  loader: async () => {
    // Async loader yields to browser, allowing paint before continuing
    await Promise.resolve();
  },
  component: RootComponent,
});

